import React, { forwardRef, useMemo } from 'react'
import type { Chart as ChartJS, ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import type { Context } from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import type { Month } from '../../types'
import { formatPercent, longMonth, shortMonth } from '../../utils'
import { ChartBox } from './ChartBox'
import { AXIS_FONT, COLOURS, FONTS, monthTicks, percentTick } from './theme'

const PLUGINS = [ChartDataLabels]
const HEIGHT = 280

// Lowest band first: orange → peach → yellow → lime → forest.
const BAND_COLOURS = [COLOURS.orange, COLOURS.apricot, COLOURS.yellow, COLOURS.lime, COLOURS.forest]
const BAND_TEXT = [COLOURS.ink, COLOURS.ink, COLOURS.ink, COLOURS.ink, COLOURS.white]

type Props = {
  // Each month's share of people in each band, lowest band first.
  series: { month: Month; shares: number[] }[]
  names: string[]
  ranges: string[]
  label: string
}

const halfBar = (context: Context) => {
  const bar = context.chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex]
  return bar ? (bar.getProps(['width'], true) as { width: number }).width / 2 : 0
}

// One stacked column per month, the best band at the bottom, a number in
// every segment and the band names beside the latest column.
export const BandsChart = forwardRef<ChartJS<'bar'> | undefined, Props>(({ series, names, ranges, label }, ref) => {
  const [data, animated, still] = useMemo(() => {
    const last = series.length - 1

    const chartData = {
      labels: series.map((point) => shortMonth(point.month)),
      // Chart.js stacks the first dataset at the bottom: Highly likely goes first.
      datasets: names
        .map((name, band) => ({
          label: name,
          data: series.map((point) => point.shares[band]),
          backgroundColor: BAND_COLOURS[band],
          borderColor: COLOURS.white,
          borderWidth: 1,
          borderRadius: 2,
          borderSkipped: false,
          maxBarThickness: 40,
          datalabels: {
            labels: {
              value: {
                display: (context: Context) => Number(context.dataset.data[context.dataIndex]) > 0,
                color: BAND_TEXT[band],
                font: { family: FONTS.heading, size: 12, weight: 600 },
                formatter: (value: number) => String(Math.round(value)),
              },
              name: {
                // An empty band has no segment to sit beside; its name would land
                // on top of the next one. The panel's table still lists it.
                display: (context: Context) => context.dataIndex === last && Number(context.dataset.data[context.dataIndex]) > 0,
                anchor: 'center' as const,
                align: 'right' as const,
                offset: (context: Context) => halfBar(context) + 10,
                color: COLOURS.ink,
                font: { family: FONTS.heading, size: 12, weight: 600 },
                formatter: () => `${name} · ${ranges[band]}`,
              },
            },
          },
        }))
        .reverse(),
    }

    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 170 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => longMonth(series[items[0].dataIndex].month),
            label: (item) => `${item.dataset.label}: ${formatPercent(item.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false, drawBorder: false },
          ticks: monthTicks(series.length),
        },
        y: {
          stacked: true,
          min: 0,
          max: 100,
          grid: { color: COLOURS.grid, drawBorder: false, drawTicks: false },
          ticks: { stepSize: 50, padding: 10, color: COLOURS.grey600, font: AXIS_FONT, callback: percentTick },
        },
      },
    }

    return [chartData, options, { ...options, animation: false as const }]
  }, [series, names, ranges])

  return (
    <ChartBox height={HEIGHT} label={label}>
      {(animate) => <Bar ref={ref} data={data} options={animate ? animated : still} plugins={PLUGINS} />}
    </ChartBox>
  )
})
